/**
 * The two answers a board write can give besides success, which are answered differently and
 * are never rolled into one.
 *
 * A refusal is visible and carries the server's own wording; retrying it changes nothing. A
 * failure is retryable and has no wording worth repeating. Turning the first into the second
 * loses the reason, which is the whole of what makes a refusal actionable.
 *
 * Part of the adapter contract rather than of any one transport, so `SavedViewBoard` can tell
 * them apart without naming an endpoint or knowing a status code.
 */

/** Whatever else the server sent with a refusal, keyed as the wire spells it. */
export type BoardMoveRefusalDetails = Readonly<Record<string, unknown>>

/** A write the server declined, carrying the reason it gave and the field responsible. */
export class BoardMoveRefusedError extends Error {
  /** The responsible field's registry identifier, absent when no single field is. */
  readonly field?: string
  /** The kind of refusal, for a client that has a richer way to explain that kind. */
  readonly code?: string
  readonly details: BoardMoveRefusalDetails

  constructor(
    message: string,
    field?: string,
    {code, details = {}}: {code?: string; details?: BoardMoveRefusalDetails} = {},
  ) {
    super(message)
    this.name = 'BoardMoveRefusedError'
    this.field = field
    this.code = code
    this.details = details
  }
}

/** A write that did not complete. Retryable, and carries no server wording. */
export class BoardMoveFailedError extends Error {
  constructor(message: string = 'Could not move this item. Please try again.') {
    super(message)
    this.name = 'BoardMoveFailedError'
  }
}
